import { Link } from 'react-router-dom';
import { Shield, Clock, UserCheck, FileCheck, HeartPulse, Trophy, ArrowRightLeft, ArrowRight } from 'lucide-react';

const PRODUCTS = [
  {
    to: '/quote',
    icon: HeartPulse,
    accent: '#4CD6BE',
    badge: 'Critical Injury',
    title: 'Critical Injury Insurance',
    desc: 'Protect an athlete\u2019s NIL contract value if a covered injury ends or interrupts their season.',
    cta: 'Request a quote',
    testid: 'product-critical-injury',
  },
  {
    to: '/quote-performance-bonus',
    icon: Trophy,
    accent: '#F59E0B',
    badge: 'Performance Bonus',
    title: 'Performance Bonus & Incentive',
    desc: 'Insure performance bonus pools against team milestone triggers \u2014 wins, championships, and tournament runs.',
    cta: 'Request a quote',
    testid: 'product-performance-bonus',
  },
  {
    to: '/quote-transfer',
    icon: ArrowRightLeft,
    accent: '#6B5CE7',
    badge: 'Athlete Transfer',
    title: 'Athlete Transfer Insurance',
    desc: 'Protect your NIL investment when an athlete transfers to another program. Coverage built around contract value.',
    cta: 'Request a quote',
    testid: 'product-transfer',
  },
];

const InsuranceHomePage = () => {
  const steps = [
    { num: '01', icon: FileCheck, title: 'Submit your request', desc: 'Share contact info, athlete details, and NIL contract value. Takes about 5 minutes.' },
    { num: '02', icon: UserCheck, title: 'Advisor reviews it', desc: 'A licensed Players Health advisor reviews your submission — no automated guesses, no sticker price.' },
    { num: '03', icon: Clock, title: 'Get your quote', desc: 'We follow up within 1–2 business days with a personalized, bindable quote built for your situation.' },
    { num: '04', icon: Shield, title: 'Bind & manage', desc: 'Sign, pay, and we keep an eye on the policy through the season.' },
  ];

  const needs = [
    'Your name, email, and phone (so we can reach you)',
    'Athlete\u2019s name, school, sport, and position',
    'Best estimate of NIL contract value (USD)',
    'Whether the athlete had a significant injury in the past 24 months',
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Nav */}
      <nav className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/nil-resources/logos/logo-transparent.png" alt="Players Health" className="h-8" />
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-[#6B7280] text-sm font-medium hover:text-[#0F172A] transition hidden sm:inline">Internal</Link>
            <a
              href="#coverage"
              className="inline-flex items-center px-4 py-2 bg-[#4CD6BE] text-[#0A0A0A] text-sm font-semibold rounded-md hover:bg-[#3BC0A9] transition"
              data-testid="nav-cta"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </nav>

      {/* Hero — Dark */}
      <section className="relative bg-[#0A0A0A] overflow-hidden">
        {/* Background photos */}
        <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 opacity-15">
          <div className="bg-cover bg-center" style={{ backgroundImage: "url('/nil-resources/photos/photo-basketball.png')" }} />
          <div className="bg-cover bg-center" style={{ backgroundImage: "url('/nil-resources/photos/photo-soccer.jpg')" }} />
          <div className="bg-cover bg-center hidden md:block" style={{ backgroundImage: "url('/nil-resources/photos/photo-tennis-teal.jpg')" }} />
          <div className="bg-cover bg-center hidden md:block" style={{ backgroundImage: "url('/nil-resources/photos/photo-tennis-topview.png')" }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/60 to-[#0A0A0A]" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-24">
          <p className="text-[#7B6FE8] text-[11px] font-semibold uppercase tracking-[0.12em] mb-4">
            NIL Insurance · Powered by Players Health
          </p>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6 max-w-3xl"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
          >
            Protect the value of every NIL deal.
          </h1>
          <p className="text-[#94A3B8] text-lg leading-relaxed max-w-2xl mb-10">
            Critical injury, performance bonus, and athlete transfer coverage built to protect NIL contract value. Request a personalized quote — no commitment.
          </p>
          <div className="flex flex-wrap items-center gap-4 mb-12">
            <a
              href="#coverage"
              className="inline-flex items-center px-6 py-3 bg-[#4CD6BE] text-[#0A0A0A] font-semibold rounded-md hover:bg-[#3BC0A9] transition text-base"
              data-testid="hero-cta"
            >
              Request a Quote →
            </a>
            <a href="#how" className="inline-flex items-center px-6 py-3 bg-[#1F1F1F] text-white font-medium rounded-md hover:bg-[#2A2A2A] transition text-base">
              How it works
            </a>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-[#6B7280]">
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#4CD6BE]" /> Personalized quote in 1–2 business days</span>
            <span className="flex items-center gap-2"><UserCheck className="w-4 h-4 text-[#4CD6BE]" /> No automated underwriting</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#4CD6BE]" /> Licensed Players Health advisor</span>
          </div>
        </div>
      </section>

      {/* Gradient divider */}
      <div className="h-[3px] bg-gradient-to-r from-[#6B5CE7] via-[#4ECDC4] to-[#FFD93D]" />

      {/* Coverage Types */}
      <section id="coverage" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#7B6FE8] text-[11px] font-semibold uppercase tracking-[0.12em] mb-3">Choose your coverage</p>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4 max-w-xl"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
          >
            Three ways to protect NIL value
          </h2>
          <p className="text-[#6B7280] text-base leading-relaxed max-w-2xl mb-12">
            Pick the coverage that fits your situation. Each request goes straight to a licensed Players Health advisor.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRODUCTS.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                data-testid={p.testid}
                className="group bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-6 flex flex-col hover:border-[#CBD5E1] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all"
              >
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${p.accent}22` }}>
                  <p.icon className="w-5 h-5" style={{ color: p.accent }} />
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] mb-2" style={{ color: p.accent }}>{p.badge}</p>
                <h3 className="text-[#0F172A] font-bold text-lg mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
                  {p.title}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-5 flex-1">{p.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] group-hover:gap-2.5 transition-all" style={{ color: p.accent }}>
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#7B6FE8] text-[11px] font-semibold uppercase tracking-[0.12em] mb-3">How it works</p>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4 max-w-xl"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
          >
            Four steps, no auto-magic
          </h2>
          <p className="text-[#6B7280] text-base leading-relaxed max-w-2xl mb-12">
            Instant quotes don't work for NIL coverage — the contracts vary too much and the athlete profile matters. So a human advisor builds yours.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step) => (
              <div key={step.num} className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#EEF0FF] flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-[#6B5CE7]" />
                  </div>
                  <span className="text-[#D1D5DB] font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{step.num}</span>
                </div>
                <h3 className="text-[#0F172A] font-bold text-base mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
                  {step.title}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you'll need */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#7B6FE8] text-[11px] font-semibold uppercase tracking-[0.12em] mb-3">Prep before you start</p>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
          >
            What you'll need
          </h2>
          <p className="text-[#6B7280] text-base leading-relaxed max-w-2xl mb-8">
            Have these handy before you start the form. The whole thing takes about five minutes if you have them in front of you.
          </p>
          <ul className="space-y-3 mb-16 max-w-xl">
            {needs.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#4CD6BE]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-[#4CD6BE]" />
                </div>
                <span className="text-[#374151] text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>

          {/* Bottom CTA */}
          <div className="border-t border-[#E5E7EB] pt-12 text-center">
            <h2
              className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-3"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}
            >
              Ready when you are.
            </h2>
            <p className="text-[#6B7280] text-base mb-6">
              Submit your request and a Players Health advisor will be in touch within two business days.
            </p>
            <a
              href="#coverage"
              className="inline-flex items-center px-6 py-3 bg-[#4CD6BE] text-[#0A0A0A] font-semibold rounded-md hover:bg-[#3BC0A9] transition text-base"
              data-testid="bottom-cta"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsuranceHomePage;
